import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, UserCog } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/profile")({
  head: () => ({
    meta: [
      { title: "My Admin Profile — Coffman Cove Lodging" },
      { name: "description", content: "Manage your own staff sign-in password and see your admin permissions." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminProfile,
});

function AdminProfile() {
  const [email, setEmail] = useState<string | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [allowlist, setAllowlist] = useState<string[]>([]);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data: userRes } = await supabase.auth.getUser();
    const user = userRes.user;
    setEmail(user?.email ?? null);
    if (!user) return;

    const [{ data: roleRows }, { data: allowRows }] = await Promise.all([
      supabase.from("user_roles").select("role").eq("user_id", user.id),
      supabase.from("admin_allowlist").select("email").order("email"),
    ]);
    setRoles((roleRows ?? []).map((r) => r.role as string));
    setAllowlist((allowRows ?? []).map((r) => r.email as string));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function changePassword(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    if (next.length < 8) {
      toast.error("Use at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords do not match");
      return;
    }
    setSaving(true);
    try {
      // Re-check the current password so a left-open browser can't reset the account.
      const { error: signInErr } = await supabase.auth.signInWithPassword({
        email,
        password: current,
      });
      if (signInErr) {
        toast.error("Current password is incorrect");
        return;
      }
      const { error } = await supabase.auth.updateUser({ password: next });
      if (error) {
        toast.error(error.message);
        return;
      }
      setCurrent("");
      setNext("");
      setConfirm("");
      toast.success("Password updated — it applies only to your account");
    } catch {
      toast.error("Could not update your password");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-12">
      <header>
        <div className="flex items-center gap-2 text-[var(--color-ember)]">
          <UserCog size={18} aria-hidden />
          <span className="text-xs font-semibold uppercase tracking-[0.25em]">My account</span>
        </div>
        <h1 className="mt-2 font-display text-3xl">Admin profile</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Each staff member signs in with their own account. Changes here affect only{" "}
          <strong>{email ?? "your account"}</strong>.
        </p>
      </header>

      <section className="mt-8 rounded-xl border border-border bg-card p-5">
        <h2 className="font-display text-lg">Your access</h2>
        <dl className="mt-4 grid gap-4 sm:grid-cols-2">
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Signed in as</dt>
            <dd className="text-sm">{email ?? "…"}</dd>
          </div>
          <div>
            <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">Permissions</dt>
            <dd className="mt-1 flex flex-wrap gap-2">
              {roles.length === 0 ? (
                <span className="text-sm text-muted-foreground">No roles</span>
              ) : (
                roles.map((r) => (
                  <span
                    key={r}
                    className="inline-flex items-center gap-1 rounded-full bg-[var(--color-ember)]/12 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-ember)]"
                  >
                    <ShieldCheck size={12} aria-hidden /> {r}
                  </span>
                ))
              )}
            </dd>
          </div>
        </dl>
        <p className="mt-4 text-xs text-muted-foreground">
          Admin access is granted by email. Staff accounts approved for the admin area:
        </p>
        <ul className="mt-2 space-y-1 text-sm">
          {allowlist.map((a) => (
            <li key={a} className={a.toLowerCase() === (email ?? "").toLowerCase() ? "font-semibold" : ""}>
              {a}
              {a.toLowerCase() === (email ?? "").toLowerCase() ? " (you)" : ""}
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-6 rounded-xl border border-border bg-card p-5">
        <h2 className="flex items-center gap-2 font-display text-lg">
          <KeyRound size={16} aria-hidden /> Change my password
        </h2>
        <form onSubmit={(e) => void changePassword(e)} className="mt-4 grid gap-4">
          <label className="grid gap-1 text-sm">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Current password</span>
            <input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              className="rounded-md border border-input bg-background px-3 py-2"
            />
          </label>
          <label className="grid gap-1 text-sm">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">New password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              minLength={8}
              required
              className="rounded-md border border-input bg-background px-3 py-2"
            />
          </label>
          <label className="grid gap-1 text-sm">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Confirm new password</span>
            <input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={8}
              required
              className="rounded-md border border-input bg-background px-3 py-2"
            />
          </label>
          <button
            type="submit"
            disabled={saving}
            className="justify-self-start rounded-md bg-[var(--color-ember)] px-4 py-2 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] disabled:opacity-60"
          >
            {saving ? "Saving…" : "Update password"}
          </button>
        </form>
        <p className="mt-3 text-xs text-muted-foreground">
          This only changes your own sign-in. Other staff accounts keep their own passwords.
        </p>
      </section>
    </div>
  );
}
